import { useState } from "react";

import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCheck, FiEdit2, FiPlus, FiTrash2, FiUsers } from "react-icons/fi";

import {
  ContactInfo,
  ContactsInfo,
} from "../../../../types/registerBranchDataTypes";
import { useBranch } from "../../../../data/user";
import { DataTable, EmptyState } from "../../../ui";

interface contactsStepProps {
  savedData: ContactsInfo;
  updateData: (data: ContactsInfo) => void;
}

interface UpdatedContactsInfo extends ContactInfo {
  index: number;
}

const titleOptions = [
  "Owner",
  "Manager",
  "Partner",
  "Finance Manager",
  "Accounts Payable",
  "Purchasing Manager",
];
const ContactsStep: React.FC<contactsStepProps> = ({
  savedData,
  updateData,
}) => {
  const [selectedUser, setSelectedUser] = useState<UpdatedContactsInfo>({
    title: "",
    name: "",
    email: "",
    phone: "",
    mobile: "",
    shareholder_percentage: "",
    index: -1,
  });

  const [editEnabled, setEditEnabled] = useState<boolean>(false);
  const toast = useToast();

  const { branch } = useBranch();

  return (
    <Box>
      {savedData && savedData.length > 0 ? (
        <DataTable size="sm" label="Contacts table">
          <Table>
            <Thead>
              <Tr>
                <Th scope="col">Title</Th>
                <Th scope="col">Name</Th>
                <Th scope="col">Email</Th>
                <Th scope="col">Phone</Th>
                <Th scope="col">Mobile</Th>
                <Th scope="col" isNumeric>
                  Shareholder Percentage
                </Th>
                <Th scope="col">
                  <VisuallyHidden>Actions</VisuallyHidden>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {savedData.map((user, index) => (
                <Tr key={index}>
                  <Td>{user.title}</Td>
                  <Td fontWeight={600}>{user.name}</Td>
                  <Td>{user.email}</Td>
                  <Td fontWeight={600}>{user.phone}</Td>
                  <Td fontWeight={600}>{user.mobile}</Td>
                  <Td isNumeric>{user.shareholder_percentage}</Td>
                  <Td>
                    <ButtonGroup
                      size="xs"
                      spacing={2}
                      display="flex"
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="secondary"
                        leftIcon={<FiEdit2 />}
                        onClick={() => {
                          setSelectedUser({ ...user, index: index });
                          setEditEnabled(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger-soft"
                        leftIcon={<FiTrash2 />}
                        onClick={() => {
                          updateData(savedData.filter((_, i) => i !== index));
                        }}
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      ) : (
        <EmptyState
          icon={FiUsers}
          title="No contacts added"
          description='Fill in the form below and press "Add" to list who we should reach at this branch.'
          py={6}
          headingAs="p"
        />
      )}
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
        gap={5}
        mt={6}
      >
        <GridItem colSpan={1}>
          <FormControl
            isInvalid={!selectedUser?.title} // validation
          >
            <FormLabel>Title</FormLabel>
            <Select
              placeholder="Select Title"
              value={selectedUser?.title}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  title: e.target.value,
                });
              }}
            >
              {titleOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input
              type="text"
              isInvalid={!selectedUser?.name} // validation
              value={selectedUser?.name}
              placeholder={branch === "UAE" ? "Text only" : ""}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  name: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Phone</FormLabel>
            <Input
              isInvalid={!selectedUser?.phone}
              value={selectedUser?.phone}
              placeholder={
                branch === "UAE" ? "041234567 (numbers only, no spaces)" : ""
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  phone: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              isInvalid={!selectedUser?.email} // validation
              value={selectedUser?.email}
              placeholder={branch === "UAE" ? "test@test.com" : ""}
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  email: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>

        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Mobile</FormLabel>
            <Input
              value={selectedUser?.mobile}
              placeholder={
                branch === "UAE" ? "0501234567 (numbers only, no spaces)" : ""
              }
              onChange={(e) => {
                setSelectedUser({
                  ...selectedUser,
                  mobile: e.target.value,
                });
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={1}>
          <FormControl>
            <FormLabel>Shareholder Percentage</FormLabel>
            <InputGroup>
              <Input
                value={selectedUser?.shareholder_percentage}
                onChange={(e) => {
                  setSelectedUser({
                    ...selectedUser,
                    shareholder_percentage: e.target.value,
                  });
                }}
                pr="2.5rem" // add some padding to prevent overlap with the % symbol
              />
              <InputRightElement pointerEvents="none" width="2.5rem">
                <span>%</span>
              </InputRightElement>
            </InputGroup>
          </FormControl>
        </GridItem>
        <GridItem colSpan={1} alignSelf="end">
          <Button
            variant="secondary"
            w="full"
            leftIcon={editEnabled ? <FiCheck /> : <FiPlus />}
            isDisabled={
              !selectedUser?.name ||
              !selectedUser?.title ||
              !selectedUser?.email ||
              !selectedUser?.phone
            }
            onClick={() => {
              if (editEnabled) {
                // update the data and don't forget to remove the index  but after updating the data
                const { index, ...newUser } = selectedUser;
                updateData(
                  savedData.map((user, i) => (i === index ? newUser : user))
                );

                toast({
                  title: "Contact Updated",
                  description: "Contact has been updated successfully",
                  status: "warning",
                  duration: 9000,
                  isClosable: true,
                });

                setEditEnabled(false);
              } else {
                // remove the index before updating the data
                const { index, ...newUser } = selectedUser;
                updateData([...savedData, newUser]);

                toast({
                  title: "Contact Added",
                  description: "Contact has been added successfully",
                  status: "success",
                  duration: 9000,
                  isClosable: true,
                });
              }
              setSelectedUser({
                title: "",
                name: "",
                email: "",
                phone: "",
                mobile: "",
                shareholder_percentage: "",
                index: -1,
              });
            }}
          >
            {editEnabled ? "Save" : "Add"}
          </Button>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ContactsStep;
